/**
 * Parse Mermaid flowchart syntax into React Flow nodes and edges
 */

import type {
  MermaidDirection,
  MermaidEdge,
  MermaidEdgeArrow,
  MermaidEdgeStyle,
  MermaidNode,
  MermaidNodeShape,
  ParsedMermaid,
} from "./mermaid-types"

import { EDGE_SYNTAXES, SHAPE_SYNTAXES } from "./mermaid-types"

const DIRECTION_PATTERN = /^(flowchart|graph)\s+(TB|TD|BT|RL|LR)\s*$/i
const SUBGRAPH_START_PATTERN = /^subgraph\s+(\w+)(?:\s*\[([^\]]+)\])?\s*$/i
const SUBGRAPH_END_PATTERN = /^end\s*$/i

const SHAPE_SYNTAXES_BY_OPEN_LENGTH = [...SHAPE_SYNTAXES].sort(
  (a, b) => b.open.length - a.open.length
)
const EDGE_OPERATOR_PATTERN = /^([-=~<>.]+(?:\|[^|]*\|)?[-=~<>.]*)/

type ParsedNodeToken = {
  id: string
  label?: string
  shape?: MermaidNodeShape
  nextIndex: number
}

function skipWhitespace(text: string, startIndex: number): number {
  let index = startIndex
  while (index < text.length && /\s/.test(text[index])) {
    index += 1
  }
  return index
}

function unescapeLabel(label: string): string {
  let result = label.trim()

  // Strip surrounding quotes if present
  if (result.startsWith('"') && result.endsWith('"') && result.length >= 2) {
    result = result.slice(1, -1)
  }

  // Unescape escaped characters: \" -> " and \\ -> \
  result = result.replace(/\\"/g, '"').replace(/\\\\/g, "\\")

  return result
}

function parseNodeToken(line: string, startIndex: number): ParsedNodeToken | null {
  let index = skipWhitespace(line, startIndex)
  const idMatch = line.slice(index).match(/^(\w+)/)
  if (!idMatch) return null

  const nodeId = idMatch[1]
  index += nodeId.length

  for (const syntax of SHAPE_SYNTAXES_BY_OPEN_LENGTH) {
    if (!line.startsWith(syntax.open, index)) continue

    const labelStartIndex = index + syntax.open.length
    const closeIndex = line.indexOf(syntax.close, labelStartIndex)
    if (closeIndex === -1) continue

    const rawLabel = line.slice(labelStartIndex, closeIndex).trim()
    const label = unescapeLabel(rawLabel)
    return {
      id: nodeId,
      label: label || nodeId,
      shape: syntax.shape,
      nextIndex: closeIndex + syntax.close.length,
    }
  }

  return { id: nodeId, nextIndex: index }
}

function parseEdgeOperator(
  line: string,
  startIndex: number
): { edgeText: string; nextIndex: number } | null {
  const index = skipWhitespace(line, startIndex)
  const match = line.slice(index).match(EDGE_OPERATOR_PATTERN)
  if (!match) return null

  return { edgeText: match[1], nextIndex: index + match[0].length }
}

function parseEdgeStyle(edgeText: string): {
  style: MermaidEdgeStyle
  sourceArrow: MermaidEdgeArrow
  targetArrow: MermaidEdgeArrow
  label?: string
} {
  let label: string | undefined
  let cleanEdge = edgeText

  const labelMatch = edgeText.match(/\|([^|]*)\|/)
  if (labelMatch) {
    label = unescapeLabel(labelMatch[1])
    cleanEdge = edgeText.replace(/\|[^|]*\|/, "")
  }

  for (const syntax of EDGE_SYNTAXES) {
    if (syntax.pattern.test(cleanEdge)) {
      return {
        style: syntax.style,
        sourceArrow: syntax.sourceArrow,
        targetArrow: syntax.targetArrow,
        label,
      }
    }
  }

  return { style: "solid", sourceArrow: "none", targetArrow: "arrow", label }
}

function calculateNodePosition(
  nodeIndex: number,
  direction: MermaidDirection,
  nodesPerRow: number = 4
): { x: number; y: number } {
  const horizontalSpacing = 200
  const verticalSpacing = 120

  const row = Math.floor(nodeIndex / nodesPerRow)
  const col = nodeIndex % nodesPerRow

  switch (direction) {
    case "LR":
      return { x: row * horizontalSpacing, y: col * verticalSpacing }
    case "RL":
      return { x: -row * horizontalSpacing, y: col * verticalSpacing }
    case "BT":
      return { x: col * horizontalSpacing, y: -row * verticalSpacing }
    case "TB":
    case "TD":
    default:
      return { x: col * horizontalSpacing, y: row * verticalSpacing }
  }
}

export function parseMermaidCode(code: string): ParsedMermaid {
  const lines = code.split("\n").map((line) => line.trim())
  const result: ParsedMermaid = {
    direction: "TD",
    nodes: [],
    edges: [],
    subgraphs: new Map(),
  }

  const nodeMap = new Map<string, MermaidNode>()
  let currentSubgraph: string | null = null
  let nodeIndex = 0

  const reservedNodeIds = new Set([
    "flowchart",
    "graph",
    "subgraph",
    "end",
    "style",
    "classdef",
    "class",
  ])

  function upsertNode(nodeId: string, label?: string, shape?: MermaidNodeShape) {
    const existing = nodeMap.get(nodeId)
    if (existing) {
      const nextLabel = label ?? existing.data.label
      const nextShape = shape ?? existing.data.shape
      if (nextLabel !== existing.data.label || nextShape !== existing.data.shape) {
        existing.data = { ...existing.data, label: nextLabel, shape: nextShape }
      }
      return
    }

    const position = calculateNodePosition(nodeIndex++, result.direction)
    const node: MermaidNode = {
      id: nodeId,
      type: "mermaid",
      position,
      data: {
        label: label ?? nodeId,
        shape: shape ?? "rectangle",
        subgraph: currentSubgraph || undefined,
      },
    }

    nodeMap.set(nodeId, node)
    if (currentSubgraph) {
      result.subgraphs.get(currentSubgraph)?.nodeIds.push(nodeId)
    }
  }

  function upsertNodeIfNotReserved(nodeId: string, label?: string, shape?: MermaidNodeShape) {
    if (!reservedNodeIds.has(nodeId.toLowerCase())) {
      upsertNode(nodeId, label, shape)
    }
  }

  for (const line of lines) {
    if (!line || line.startsWith("%%")) continue

    const directionMatch = line.match(DIRECTION_PATTERN)
    if (directionMatch) {
      result.direction = directionMatch[2].toUpperCase() as MermaidDirection
      continue
    }

    const subgraphStartMatch = line.match(SUBGRAPH_START_PATTERN)
    if (subgraphStartMatch) {
      const subgraphId = subgraphStartMatch[1]
      const subgraphLabel = subgraphStartMatch[2] || subgraphId
      currentSubgraph = subgraphId
      result.subgraphs.set(subgraphId, { id: subgraphId, label: subgraphLabel, nodeIds: [] })
      continue
    }

    if (SUBGRAPH_END_PATTERN.test(line)) {
      currentSubgraph = null
      continue
    }

    const sourceToken = parseNodeToken(line, 0)
    if (!sourceToken) continue

    const edgeOperator = parseEdgeOperator(line, sourceToken.nextIndex)
    if (edgeOperator) {
      const targetToken = parseNodeToken(line, edgeOperator.nextIndex)
      if (targetToken) {
        upsertNodeIfNotReserved(sourceToken.id, sourceToken.label, sourceToken.shape)
        upsertNodeIfNotReserved(targetToken.id, targetToken.label, targetToken.shape)

        const { style, sourceArrow, targetArrow, label } = parseEdgeStyle(edgeOperator.edgeText)
        const edge: MermaidEdge = {
          id: `${sourceToken.id}-${targetToken.id}-${result.edges.length}`,
          source: sourceToken.id,
          target: targetToken.id,
          type: "mermaidEdge",
          data: { style, sourceArrow, targetArrow, label },
          label,
        }
        result.edges.push(edge)
        continue
      }
    }

    const isLineOnlyNode = skipWhitespace(line, sourceToken.nextIndex) >= line.length
    if (isLineOnlyNode) {
      upsertNodeIfNotReserved(sourceToken.id, sourceToken.label, sourceToken.shape)
    }
  }

  result.nodes = Array.from(nodeMap.values())
  return result
}

export function detectDiagramType(
  code: string
): "flowchart" | "sequence" | "class" | "state" | "er" | "gantt" | "pie" | "other" {
  const trimmed = code.trim().toLowerCase()

  if (trimmed.startsWith("flowchart") || trimmed.startsWith("graph")) return "flowchart"
  if (trimmed.startsWith("sequencediagram")) return "sequence"
  if (trimmed.startsWith("classdiagram")) return "class"
  if (trimmed.startsWith("statediagram")) return "state"
  if (trimmed.startsWith("erdiagram")) return "er"
  if (trimmed.startsWith("gantt")) return "gantt"
  if (trimmed.startsWith("pie")) return "pie"

  return "other"
}
